/**
 * Copyright (C) Mairistem
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 *
 * Proprietary and confidential
 */

import dayjs from 'dayjs';

import { TypeDateNaissance } from '@/enums';

/**
 * Date de naissance d'un individu de convention : l'API la stocke en date
 * complète (`naissanceDate`, partie inconnue au 1er) accompagnée de sa
 * précision (`naissanceTypeDate`, libellé de ai_nai_type_date).
 */

/** Libellés de ActeIndividu::$naissanceTypeDateCases côté API */
const LIBELLES: Record<string, TypeDateNaissance> = {
  complete: TypeDateNaissance.COMPLET,
  'Mois,Année': TypeDateNaissance.MOIS_ANNEE,
  Année: TypeDateNaissance.ANNEE,
};

const LIBELLE_PAR_TYPE: Record<TypeDateNaissance, string> = {
  [TypeDateNaissance.COMPLET]: 'complete',
  [TypeDateNaissance.MOIS_ANNEE]: 'Mois,Année',
  [TypeDateNaissance.ANNEE]: 'Année',
};

/** Précision relue de l'API ; « non renseigné » ou absente : date complète */
export const typeDateDepuisApi = (
  naissanceTypeDate?: string | number | null,
): TypeDateNaissance => {
  if (naissanceTypeDate === 2 || naissanceTypeDate === '2') {
    return TypeDateNaissance.MOIS_ANNEE;
  }
  if (naissanceTypeDate === 3 || naissanceTypeDate === '3') {
    return TypeDateNaissance.ANNEE;
  }
  return LIBELLES[String(naissanceTypeDate ?? '')] ?? TypeDateNaissance.COMPLET;
};

/** Jour / mois / année et mode de saisie, sans inventer les parties inconnues */
export const decomposerNaissance = (
  naissanceDate?: string | null,
  naissanceTypeDate?: string | number | null,
) => {
  const date = naissanceDate ? dayjs(naissanceDate) : null;

  if (!date?.isValid()) {
    return {
      jourNaissance: undefined,
      moisNaissance: undefined,
      anneeNaissance: undefined,
      typeDateNaissance: TypeDateNaissance.COMPLET,
    };
  }

  const typeDateNaissance = typeDateDepuisApi(naissanceTypeDate);

  return {
    jourNaissance:
      typeDateNaissance === TypeDateNaissance.COMPLET ? date.date() : undefined,
    moisNaissance:
      typeDateNaissance === TypeDateNaissance.ANNEE
        ? undefined
        : date.month() + 1,
    anneeNaissance: date.year(),
    typeDateNaissance,
  };
};

/** Affichage : JJ/MM/AAAA, MM/AAAA ou AAAA selon la précision */
export const formaterNaissance = (
  naissanceDate?: string | null,
  naissanceTypeDate?: string | number | null,
): string => {
  const date = naissanceDate ? dayjs(naissanceDate) : null;

  if (!date?.isValid()) {
    return 'Non renseigné';
  }

  switch (typeDateDepuisApi(naissanceTypeDate)) {
    case TypeDateNaissance.MOIS_ANNEE:
      return date.format('MM/YYYY');
    case TypeDateNaissance.ANNEE:
      return date.format('YYYY');
    default:
      return date.format('DD/MM/YYYY');
  }
};

/**
 * Inverse de decomposerNaissance, pour l'enregistrement : date null tant que
 * les parties exigées par le mode ne sont pas toutes saisies.
 */
export const composerNaissance = (
  jour: number | null | undefined,
  mois: number | null | undefined,
  annee: number | null | undefined,
  typeDateNaissance: TypeDateNaissance = TypeDateNaissance.COMPLET,
) => {
  const complete =
    annee != null &&
    (typeDateNaissance === TypeDateNaissance.ANNEE ||
      (mois != null &&
        (typeDateNaissance === TypeDateNaissance.MOIS_ANNEE || jour != null)));

  if (!complete) {
    return { naissanceDate: null, naissanceTypeDate: null };
  }

  const moisEffectif =
    typeDateNaissance === TypeDateNaissance.ANNEE ? 1 : (mois as number);
  const jourEffectif =
    typeDateNaissance === TypeDateNaissance.COMPLET ? (jour as number) : 1;

  return {
    naissanceDate: dayjs(
      new Date(annee as number, moisEffectif - 1, jourEffectif),
    ).format('YYYY-MM-DD'),
    naissanceTypeDate: LIBELLE_PAR_TYPE[typeDateNaissance],
  };
};
